import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { ScrollArea } from '../components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { saveGame, loadGame, getSaveSlots, deleteSaveSlot } from '../utils/gameData';
import { FloppyDisk, FolderOpen, Trash, Calendar, Trophy, Crown, User, Plus } from '@phosphor-icons/react';
import { format } from 'date-fns';

const SaveSection = ({ title, icon: Icon, iconColor, gameState, onLoadGame, filterPrefix, headerExtras }) => {
  const [saveSlots, setSaveSlots] = useState([]);
  const [newSlotName, setNewSlotName] = useState('');
  const [message, setMessage] = useState(null);

  useEffect(() => { refreshSlots(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const refreshSlots = () => {
    const all = getSaveSlots();
    if (filterPrefix) setSaveSlots(all.filter(s => s.name.startsWith(filterPrefix)));
    else setSaveSlots(all);
  };

  const handleSave = () => {
    const prefix = filterPrefix || '';
    const slotName = prefix + (newSlotName.trim() || `save_${Date.now()}`);
    saveGame(gameState, slotName);
    setNewSlotName('');
    refreshSlots();
    showMessage('Saved successfully!', 'success');
  };

  const handleLoad = (slotName) => {
    const loaded = loadGame(slotName);
    if (loaded) {
      onLoadGame(loaded);
      showMessage(`Loaded: ${slotName.replace(filterPrefix || '', '')}`, 'success');
    } else {
      showMessage('Failed to load', 'error');
    }
  };

  const handleDelete = (slotName) => {
    deleteSaveSlot(slotName);
    refreshSlots();
    showMessage('Deleted', 'info');
  };

  const showMessage = (text, type) => {
    setMessage({ text, type });
    setTimeout(() => setMessage(null), 3000);
  };

  const hasState = gameState?.teams?.length > 0;

  return (
    <div className="space-y-4">
      {message && (
        <div className={`p-3 rounded-sm border animate-slide-in text-sm
          ${message.type === 'success' ? 'bg-[#32D74B]/10 border-[#32D74B] text-[#32D74B]' : ''}
          ${message.type === 'error' ? 'bg-[#FF3B30]/10 border-[#FF3B30] text-[#FF3B30]' : ''}
          ${message.type === 'info' ? 'bg-[#007AFF]/10 border-[#007AFF] text-[#007AFF]' : ''}`}>
          {message.text}
        </div>
      )}

      {/* Current state info */}
      <div className="card-surface rounded-sm p-4">
        <h3 className="font-heading font-bold text-white uppercase tracking-wide mb-3 flex items-center gap-2">
          <Icon size={16} className={iconColor} weight="fill" /> Current {title}
        </h3>
        {hasState ? (
          <div className="grid grid-cols-3 gap-4 text-center">
            <div>
              <p className="font-mono text-xl font-bold text-white">{gameState?.season || 1}</p>
              <p className="text-xs text-[#6E6E73] uppercase">Season</p>
            </div>
            <div>
              <p className="font-mono text-xl font-bold text-[#007AFF]">{gameState?.currentWeek || 1}/38</p>
              <p className="text-xs text-[#6E6E73] uppercase">Week</p>
            </div>
            <div>
              <p className="font-mono text-xl font-bold text-[#32D74B]">{gameState?.teams?.length || 0}</p>
              <p className="text-xs text-[#6E6E73] uppercase">Teams</p>
            </div>
          </div>
        ) : (
          <p className="text-[#6E6E73] text-sm text-center py-2">No active {title.toLowerCase()} — start a new one or load below.</p>
        )}
        {gameState?.managerMode?.teamId && (
          <div className="mt-3 pt-3 border-t border-[#2C2C35] text-center">
            <p className="text-sm text-[#FFD700]">Managing: {gameState.teams?.find(t => t.id === gameState.managerMode.teamId)?.name || 'Unknown'}</p>
          </div>
        )}
        {gameState?.playerName && (
          <div className="mt-3 pt-3 border-t border-[#2C2C35] text-center">
            <p className="text-sm text-[#32D74B]">Playing as: {gameState.playerName} ({gameState.playerPosition || 'CM'})</p>
          </div>
        )}
        {headerExtras}
      </div>

      {/* Save */}
      <div className="card-surface rounded-sm p-4">
        <h3 className="font-heading font-bold text-white uppercase tracking-wide mb-3">Save {title}</h3>
        <div className="flex gap-3">
          <Input
            data-testid={`save-${title.toLowerCase().replace(/\s+/g,'-')}-input`}
            placeholder={`Enter ${title.toLowerCase()} save name...`}
            value={newSlotName}
            onChange={(e) => setNewSlotName(e.target.value)}
            className="flex-1 bg-[#0A0A0C] border-[#2C2C35] text-white placeholder:text-[#6E6E73]"
          />
          <Button
            data-testid={`save-${title.toLowerCase().replace(/\s+/g,'-')}-btn`}
            onClick={handleSave}
            disabled={!hasState}
            className="bg-[#32D74B] hover:bg-[#32D74B]/80 text-black font-bold"
          >
            <FloppyDisk size={18} className="mr-2" /> Save
          </Button>
        </div>
      </div>

      {/* Load */}
      <div className="card-surface rounded-sm">
        <div className="border-b border-[#2C2C35] px-4 py-3">
          <h3 className="font-heading font-bold text-white uppercase tracking-wide">
            Saved {title} ({saveSlots.length})
          </h3>
        </div>
        <ScrollArea className="h-[300px]">
          <div className="p-3 space-y-2">
            {saveSlots.length === 0 ? (
              <div className="text-center py-8">
                <FolderOpen size={36} className="mx-auto text-[#6E6E73] mb-2" />
                <p className="text-[#6E6E73] text-sm">No saves found</p>
              </div>
            ) : (
              saveSlots.map(slot => (
                <div key={slot.name} data-testid={`save-slot-${slot.name}`} className="card-elevated rounded-sm p-3">
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <h4 className="font-medium text-white text-sm truncate">{slot.name.replace(filterPrefix || '', '') || '(autosave)'}</h4>
                      <div className="flex items-center gap-3 mt-1 text-xs text-[#A0A0A5]">
                        <span>S{slot.season}</span>
                        <span>W{slot.currentWeek}/38</span>
                        {slot.lastSaved && <span>{format(new Date(slot.lastSaved), 'MMM d HH:mm')}</span>}
                      </div>
                    </div>
                    <div className="flex gap-1.5">
                      <Button data-testid={`load-${slot.name}-btn`} onClick={() => handleLoad(slot.name)} className="bg-[#007AFF] hover:bg-[#0A84FF] text-white" size="sm">
                        <FolderOpen size={14} className="mr-1" /> Load
                      </Button>
                      <Button data-testid={`delete-${slot.name}-btn`} onClick={() => handleDelete(slot.name)} variant="outline" className="bg-transparent border-[#FF3B30] text-[#FF3B30] hover:bg-[#FF3B30] hover:text-white" size="sm">
                        <Trash size={14} />
                      </Button>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </ScrollArea>
      </div>
    </div>
  );
};

export const SaveLoadPage = ({ gameState, managerState, careerState, onLoadGame }) => {
  const navigate = useNavigate();
  const careerExtras = (
    <div className="mt-3 pt-3 border-t border-[#2C2C35] flex items-center justify-center gap-2">
      <Button
        data-testid="career-start-new-btn"
        onClick={() => navigate('/player-career/select')}
        className="bg-[#32D74B] hover:bg-[#2CB53E] text-black font-heading font-bold uppercase text-xs"
        size="sm"
      >
        <Plus size={14} className="mr-1.5" /> Start New Career
      </Button>
      {careerState?.playerId && (
        <Button
          data-testid="career-continue-btn"
          onClick={() => navigate('/player-career')}
          variant="outline"
          className="bg-transparent border-[#32D74B] text-[#32D74B] hover:bg-[#32D74B] hover:text-black font-heading font-bold uppercase text-xs"
          size="sm"
        >
          Continue
        </Button>
      )}
    </div>
  );

  return (
    <div className="min-h-screen bg-[#04060F] pt-20 pb-8 px-4">
      <div className="max-w-3xl mx-auto">
        <div className="mb-6">
          <h1 className="font-heading font-black text-3xl sm:text-4xl text-white uppercase tracking-wide">Save / Load</h1>
          <p className="text-[#A0A0A5] mt-1">League · Road to Glory · Player Career — each mode keeps its own saves</p>
        </div>

        <Tabs defaultValue="league" className="w-full">
          <TabsList className="bg-[#121216] border border-[#2C2C35] w-full grid grid-cols-3 mb-6">
            <TabsTrigger value="league" className="data-[state=active]:bg-[#007AFF] data-[state=active]:text-white font-heading uppercase text-sm" data-testid="save-tab-league">
              <Trophy size={16} className="mr-2" /> League
            </TabsTrigger>
            <TabsTrigger value="manager" className="data-[state=active]:bg-[#FFD700] data-[state=active]:text-black font-heading uppercase text-sm" data-testid="save-tab-manager">
              <Crown size={16} className="mr-2" /> RTG
            </TabsTrigger>
            <TabsTrigger value="player_career" className="data-[state=active]:bg-[#32D74B] data-[state=active]:text-black font-heading uppercase text-sm" data-testid="save-tab-career">
              <User size={16} className="mr-2" /> Career
            </TabsTrigger>
          </TabsList>

          <TabsContent value="league">
            <SaveSection
              title="League"
              icon={Trophy}
              iconColor="text-[#007AFF]"
              gameState={gameState}
              onLoadGame={(loaded) => onLoadGame({ ...loaded, mode: 'league' })}
              filterPrefix="league_"
            />
          </TabsContent>

          <TabsContent value="manager">
            <SaveSection
              title="Manager Career"
              icon={Crown}
              iconColor="text-[#FFD700]"
              gameState={managerState}
              onLoadGame={(loaded) => onLoadGame({ ...loaded, mode: 'manager' })}
              filterPrefix="manager_"
            />
          </TabsContent>

          <TabsContent value="player_career">
            <SaveSection
              title="Player Career"
              icon={User}
              iconColor="text-[#32D74B]"
              gameState={careerState}
              onLoadGame={(loaded) => onLoadGame({ ...loaded, mode: 'player_career' })}
              filterPrefix="player_career_"
              headerExtras={careerExtras}
            />
          </TabsContent>
        </Tabs>

        <div className="mt-6 text-sm text-[#6E6E73]">
          <p>League, Manager (RTG), and Player Career saves are completely independent.</p>
          <p>Saves are stored in your browser's local storage, scoped to your account.</p>
        </div>
      </div>
    </div>
  );
};
